import { useEffect, useRef, useState } from 'react';
import {
  BadgeDollarSign,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ReceiptText,
  ScrollText,
  Settings,
  Store,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, cn } from '@agenda-facil/ui';
import { Brand } from '../components/brand';
import { ThemeToggle } from '../components/theme-toggle';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';

interface NavigationItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const navigationSections: Array<{ label: string; items: NavigationItem[] }> = [
  {
    label: 'Plataforma',
    items: [
      { to: '/admin', label: 'Visão geral', icon: LayoutDashboard, end: true },
      { to: '/admin/barbearias', label: 'Barbearias', icon: Store },
      { to: '/admin/usuarios', label: 'Usuários', icon: UsersRound },
    ],
  },
  {
    label: 'Receita',
    items: [
      { to: '/admin/planos', label: 'Planos', icon: BadgeDollarSign },
      { to: '/admin/assinaturas', label: 'Assinaturas', icon: CreditCard },
      { to: '/admin/pagamentos', label: 'Pagamentos', icon: ReceiptText },
    ],
  },
  {
    label: 'Operação',
    items: [
      { to: '/admin/anuncios', label: 'Anúncios', icon: Megaphone },
      { to: '/admin/configuracoes', label: 'Configurações', icon: Settings },
      { to: '/admin/auditoria', label: 'Auditoria', icon: ScrollText },
    ],
  },
];

const navigation = navigationSections.flatMap((section) => section.items);
const quickNavigation = navigation.slice(0, 3);

export function AdminLayout() {
  const user = useAuthStore((state) => state.user);
  const clear = useAuthStore((state) => state.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const mobileDrawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen && !desktopMenuOpen) return undefined;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setDesktopMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !mobileMenuOpen || !mobileDrawerRef.current) return;

      const focusable = Array.from(
        mobileDrawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [desktopMenuOpen, mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>('#admin-mobile-menu-close')?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      document.querySelector<HTMLButtonElement>('#admin-mobile-menu-trigger')?.focus();
    };
  }, [mobileMenuOpen]);

  async function signOut(): Promise<void> {
    setSigningOut(true);
    try {
      await api.post('/auth/logout');
    } catch {
      // A sessão local ainda deve ser encerrada quando a API estiver indisponível.
    } finally {
      clear();
      await navigate('/login', { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-[var(--background)] md:pl-20">
      <DesktopIconRail
        menuOpen={desktopMenuOpen}
        signingOut={signingOut}
        openMenu={() => setDesktopMenuOpen(true)}
        signOut={() => void signOut()}
      />

      <button
        type="button"
        className={cn(
          'fixed inset-0 z-40 hidden cursor-default bg-[oklch(0.14_0.02_165/.18)] transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] md:block',
          desktopMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-label="Fechar menu administrativo"
        tabIndex={desktopMenuOpen ? 0 : -1}
        onClick={() => setDesktopMenuOpen(false)}
      />
      <aside
        id="admin-desktop-menu"
        className={cn(
          'fixed inset-y-0 left-0 z-50 hidden w-[292px] flex-col border-r border-[var(--border)] bg-[var(--surface)] px-5 py-6 shadow-[var(--shadow-lg)] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] md:flex',
          desktopMenuOpen
            ? 'translate-x-0 opacity-100'
            : 'pointer-events-none -translate-x-full opacity-0',
        )}
        aria-hidden={!desktopMenuOpen}
        inert={!desktopMenuOpen}
      >
        <FullSidebar
          userName={user?.name}
          userEmail={user?.email}
          signingOut={signingOut}
          closeIcon={PanelLeftClose}
          closeLabel="Recolher menu"
          closeMenu={() => setDesktopMenuOpen(false)}
          signOut={() => void signOut()}
        />
      </aside>

      <header className="sticky top-0 z-30 grid h-16 grid-cols-[44px_1fr_44px] items-center gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-3 md:hidden">
        <Button
          id="admin-mobile-menu-trigger"
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Abrir menu administrativo"
          aria-controls="admin-mobile-menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu size={21} />
        </Button>
        <Link className="justify-self-center" to="/admin" aria-label="Agenda Fácil, administração">
          <Brand />
        </Link>
        <ThemeToggle />
      </header>

      <nav
        className="sticky top-16 z-20 grid grid-cols-3 gap-1 border-b border-[var(--border)] bg-[var(--surface)] px-2 py-2 md:hidden"
        aria-label="Atalhos administrativos"
      >
        {quickNavigation.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'grid min-h-12 min-w-0 grid-cols-[18px_auto] place-content-center items-center gap-2 rounded-xl px-2 text-[11px] font-extrabold text-[var(--muted-foreground)] transition-[background-color,color] duration-200',
                isActive && 'bg-[var(--accent-soft)] text-[var(--accent)]',
              )
            }
          >
            <Icon size={17} />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        className={cn(
          'fixed inset-0 z-40 bg-[oklch(0.14_0.02_165/.34)] transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] md:hidden',
          mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-label="Fechar menu administrativo"
        tabIndex={mobileMenuOpen ? 0 : -1}
        onClick={() => setMobileMenuOpen(false)}
      />
      <aside
        ref={mobileDrawerRef}
        id="admin-mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu administrativo"
        aria-hidden={!mobileMenuOpen}
        inert={!mobileMenuOpen}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[min(88vw,332px)] flex-col border-r border-[var(--border)] bg-[var(--surface)] px-5 pb-[max(24px,env(safe-area-inset-bottom))] pt-5 shadow-[var(--shadow-lg)] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] md:hidden',
          mobileMenuOpen
            ? 'translate-x-0 opacity-100'
            : 'pointer-events-none -translate-x-full opacity-0',
        )}
      >
        <FullSidebar
          closeButtonId="admin-mobile-menu-close"
          userName={user?.name}
          userEmail={user?.email}
          signingOut={signingOut}
          closeIcon={X}
          closeLabel="Fechar menu"
          closeMenu={() => setMobileMenuOpen(false)}
          signOut={() => void signOut()}
        />
      </aside>

      <main className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-9 xl:px-10">
        <Outlet />
      </main>
    </div>
  );
}

function DesktopIconRail({
  menuOpen,
  signingOut,
  openMenu,
  signOut,
}: {
  menuOpen: boolean;
  signingOut: boolean;
  openMenu: () => void;
  signOut: () => void;
}) {
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-20 flex-col items-center border-r border-[var(--border)] bg-[var(--surface)] px-3 py-5 md:flex">
      <Link to="/admin" aria-label="Agenda Fácil, administração">
        <Brand compact />
      </Link>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="mt-5 border border-[var(--border)] bg-[var(--input)]"
        aria-label="Expandir menu administrativo"
        aria-controls="admin-desktop-menu"
        aria-expanded={menuOpen}
        onClick={openMenu}
      >
        <PanelLeftOpen size={19} />
      </Button>
      <nav
        className="mt-5 grid min-h-0 flex-1 content-start gap-1.5 overflow-y-auto"
        aria-label="Administração da plataforma"
      >
        {navigation.map((item) => (
          <RailLink key={item.to} item={item} />
        ))}
      </nav>
      <div className="mt-auto grid gap-1.5 border-t border-[var(--border)] pt-4">
        <ThemeToggle />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Sair da administração"
          title="Sair"
          disabled={signingOut}
          onClick={signOut}
        >
          <LogOut size={18} />
        </Button>
      </div>
    </aside>
  );
}

function RailLink({ item }: { item: NavigationItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      aria-label={item.label}
      className={({ isActive }) =>
        cn(
          'group relative grid size-11 place-items-center rounded-xl text-[var(--muted-foreground)] transition-[background-color,color,transform] duration-200 hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
          isActive && 'bg-[var(--brand)] text-[var(--brand-foreground)]',
        )
      }
    >
      <Icon size={18} />
      <span className="pointer-events-none absolute left-[calc(100%+12px)] z-50 hidden whitespace-nowrap rounded-lg bg-[var(--foreground)] px-2.5 py-1.5 text-xs font-extrabold text-[var(--background)] shadow-[var(--shadow-sm)] group-hover:block group-focus-visible:block">
        {item.label}
      </span>
    </NavLink>
  );
}

function FullSidebar({
  userName,
  userEmail,
  signingOut,
  closeIcon: CloseIcon,
  closeLabel,
  closeMenu,
  signOut,
  closeButtonId,
}: {
  userName?: string;
  userEmail?: string;
  signingOut: boolean;
  closeIcon: LucideIcon;
  closeLabel: string;
  closeMenu: () => void;
  signOut: () => void;
  closeButtonId?: string;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <Link className="min-w-0" to="/admin" aria-label="Agenda Fácil, administração">
          <Brand />
        </Link>
        <Button
          id={closeButtonId}
          type="button"
          variant="ghost"
          size="icon"
          aria-label={closeLabel}
          onClick={closeMenu}
        >
          <CloseIcon size={20} />
        </Button>
      </div>
      <div className="mt-5 flex items-center gap-2 px-1">
        <span className="rounded-full bg-[var(--accent-soft)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[.12em] text-[var(--accent)]">
          Admin
        </span>
        <span className="text-[11px] font-bold text-[var(--muted-foreground)]">Painel MV</span>
      </div>
      <nav className="mt-6 min-h-0 flex-1 overflow-y-auto pr-1" aria-label="Menu administrativo completo">
        {navigationSections.map((section, index) => (
          <div key={section.label} className={cn(index > 0 && 'mt-6')}>
            <p className="px-3 text-[10px] font-black uppercase tracking-[.14em] text-[var(--muted-foreground)]">
              {section.label}
            </p>
            <div className="mt-2 grid gap-1">
              {section.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-bold text-[var(--muted-foreground)] transition-[background-color,color] duration-200 hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                      isActive &&
                        'bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand)] hover:text-[var(--brand-foreground)]',
                    )
                  }
                >
                  <span className="grid size-8 shrink-0 place-items-center rounded-[10px] bg-[color-mix(in_srgb,currentColor_8%,transparent)]">
                    <Icon size={17} />
                  </span>
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="mt-5 border-t border-[var(--border)] pt-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
            {userName?.slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-extrabold">{userName}</p>
            <p className="truncate text-xs text-[var(--muted-foreground)]">{userEmail}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <ThemeToggle />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            disabled={signingOut}
            onClick={signOut}
          >
            <LogOut size={16} /> {signingOut ? 'Saindo…' : 'Sair'}
          </Button>
        </div>
      </div>
    </>
  );
}
